import React, { useEffect, useRef, useState } from "react";
import { AccessType } from "@quintype/components";
import { useSelector } from "react-redux";
import { get } from "lodash";
import { requestAccessTypeToken } from "../pages/subscription-page/auth-client.js";
import { pushUserPropertiesUpdateToDataLayer } from "./datalayer-utils";
import { RenewalModal } from "../molecules/renewal-modal";

const SyncerInner = ({ initAccessType, getSubscriptionForUser }) => {
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const isInit = useRef(false);
  const hasFired = useRef(false);
  const userdetails = useSelector((state) => get(state, ["user", "user"], {}));
  const email = userdetails?.userData?.emails?.[0] || userdetails?.email;
  const phone = userdetails?.userData?.phone || userdetails?.phone || userdetails?.phoneNumbers?.[0] || userdetails?.mobile;
  const isSubscriberRedux = useSelector((state) => get(state, ["qt", "config", "subscriber"], "false")) === "true";
  const ssoEndpoint = useSelector((state) => get(state, ["qt", "config", "publisher-attributes", "sso_endpoint"], null));

  const ensureAT = async () => {
    if (window.AccessType?.setUser) return true;
    return new Promise(r => {
      let a = 0;
      const c = setInterval(() => {
        if (window.AccessType?.setUser || ++a > 100) {
          clearInterval(c);
          r(!!window.AccessType?.setUser);
        }
      }, 100);
      if (!isInit.current) {
        isInit.current = true;
        initAccessType(() => {
          if (window.AccessType?.setUser) {
            clearInterval(c);
            r(true);
          }
        });
      }
    });
  };

  useEffect(() => {
    if (hasFired.current) return;
    
    // Ensure we don't fire empty properties before Redux hydrates for logged-in users
    const isLocalLoggedIn = typeof window !== "undefined" && !!window.localStorage.getItem("user");
    const isReduxPopulated = userdetails && Object.keys(userdetails).length > 0;
    if (isLocalLoggedIn && !isReduxPopulated) {
      return; 
    }

    let isMounted = true;
    const syncAndFetch = async () => {
      try {
        const userUuid = userdetails?.userData?.id || userdetails?.id || "NA";
        const cacheKey = `subscription_synced_${userUuid}`;

        // If we already fetched it this session for THIS user, just fire the data layer event and return
        if (sessionStorage.getItem(cacheKey) === "true") {
          const isSub = sessionStorage.getItem(`active_plan_name_${userUuid}`) !== "NA";
          pushUserPropertiesUpdateToDataLayer(
            userdetails,
            isSub,
            sessionStorage.getItem(`active_plan_name_${userUuid}`),
            sessionStorage.getItem(`active_plan_price_${userUuid}`)
          );
          
          if (sessionStorage.getItem(`recently_expired_${userUuid}`) === "true") {
            const today = new Date().toDateString();
            if (localStorage.getItem(`last_renewal_popup_${userUuid}`) !== today) {
              setShowRenewalModal(true);
              localStorage.setItem(`last_renewal_popup_${userUuid}`, today);
            }
          }
          
          hasFired.current = true;
          return;
        }
        
        if (sessionStorage.getItem(cacheKey) === "fetching") return;
        sessionStorage.setItem(cacheKey, "fetching");

        const isReady = await ensureAT();
        if (!isReady || !isMounted) return;

        const token = await requestAccessTypeToken("");
        await window.AccessType.setUser({ emailAddress: email, mobileNumber: phone, accesstypeJwt: token });
        
        const subs = await getSubscriptionForUser();
        const sList = Array.isArray(subs) ? subs : (subs?.subscriptions || []);
        
        const isSubscriptionActive = (sub) => {
          if (!sub) return false;
          if (sub.status === "active" || sub.status === "ACTIVE" || sub.status === "Active") return true;
          if (sub.status === "cancelled" || sub.status === "CANCELLED" || sub.cancelled === true) {
            const endTime = sub.end_timestamp || sub.end_date || sub.previous_end_timestamp;
            if (endTime && new Date(endTime).getTime() > Date.now()) return true;
          }
          return false;
        };

        let active = sList.find(isSubscriptionActive);
        
        if (active) {
          const durationUnit = active.duration_unit || "year";
          const durationLength = active.duration_length || 1;
          
          let planName = `${durationLength}-${durationUnit.toLowerCase().replace(/s$/, "")}`;
          if (planName === "1-month") {
            planName = "monthly";
          }

          const planPrice = active.plan_amount_cents ? (active.plan_amount_cents / 100) : "NA";
          
          sessionStorage.setItem(`active_plan_name_${userUuid}`, planName);
          sessionStorage.setItem(`active_plan_price_${userUuid}`, planPrice);
          pushUserPropertiesUpdateToDataLayer(userdetails, true, planName, planPrice);
        } else {
          sessionStorage.setItem(`active_plan_name_${userUuid}`, "NA");
          sessionStorage.setItem(`active_plan_price_${userUuid}`, "NA");
          pushUserPropertiesUpdateToDataLayer(userdetails, false, "NA", "NA");

          // --- Global Renewal Modal Logic ---
          const isRecentlyExpired = sList.some(sub => {
            const endTime = sub.end_timestamp || sub.end_date || sub.previous_end_timestamp;
            if (endTime && (sub.status === "expired" || sub.status === "cancelled" || sub.status === "EXPIRED" || sub.status === "CANCELLED" || sub.cancelled === true)) {
              const end = new Date(endTime).getTime();
              const now = Date.now();
              const sevenDays = 7 * 24 * 60 * 60 * 1000;
              return (now >= end) && (now - end <= sevenDays);
            }
            return false;
          });

          if (isRecentlyExpired) {
            sessionStorage.setItem(`recently_expired_${userUuid}`, "true");
            const today = new Date().toDateString();
            if (localStorage.getItem(`last_renewal_popup_${userUuid}`) !== today) {
              setShowRenewalModal(true);
              localStorage.setItem(`last_renewal_popup_${userUuid}`, today);
            }
          } else {
            sessionStorage.setItem(`recently_expired_${userUuid}`, "false");
          }
          // ----------------------------------
        }
        
        hasFired.current = true;
        sessionStorage.setItem(cacheKey, "true");
      } catch (err) {
        console.error("[GlobalSubscriptionSyncer] Error syncing subscription:", err);
        // Fallback to checking Redux config if API fails
        pushUserPropertiesUpdateToDataLayer(userdetails, isSubscriberRedux, "NA", "NA");
      }
    };
    
    syncAndFetch();
    
    return () => { isMounted = false; };
  }, []);

  return (
    <>
      {showRenewalModal && <RenewalModal onClose={() => setShowRenewalModal(false)} />}
    </>
  );
};

export const GlobalSubscriptionSyncer = () => {
  const qt = useSelector((state) => get(state, ["qt"], {}));
  const userdetails = useSelector((state) => get(state, ["user", "user"], {}));
  const isLoggedIn = !!userdetails?.userData?.id;
  
  const ck = get(qt, ["config", "publisher-attributes", "subscription_setup"], {});
  const aKey = ck.accesstype_key || get(qt, ["config", "publisher-attributes", "accesstype_key"], "");

  // Login and non-login DataLayer events
  useEffect(() => {
    if (typeof window === "undefined") return;

    if (!isLoggedIn) {
      const isLocalLoggedIn = typeof window !== "undefined" && !!window.localStorage.getItem("user");
      if (isLocalLoggedIn) {
        return;
      }
      pushUserPropertiesUpdateToDataLayer(null, false, "NA", "NA");
      return;
    }

    if (userdetails?.userData) {
      const userUuid = userdetails.userData.id;
      const lastLoggedInUuid = localStorage.getItem("last_logged_in_uuid");

      if (userUuid && userUuid !== "NA" && userUuid !== lastLoggedInUuid) {
        localStorage.setItem("last_logged_in_uuid", userUuid);

        const loginSource = sessionStorage.getItem("sso_login_initiated_source");
        
        let loginMethod = "email";
        const metadataLoginMethod = userdetails?.metadata?.login_method;
        
        if (metadataLoginMethod) {
          if (metadataLoginMethod === "email_login") loginMethod = "email";
          else if (metadataLoginMethod === "mobile_login" || metadataLoginMethod === "phone_login") loginMethod = "mobile no";
          else loginMethod = metadataLoginMethod;
        } else if (!loginSource) {
          loginMethod = "sso_auto_login";
        } else if (userdetails?.userData?.providers?.includes("google")) {
          loginMethod = "google_icon";
        } else if (userdetails?.userData?.phoneNumbers?.length > 0 || userdetails?.userData?.phone) {
          loginMethod = "mobile no";
        }

        let createdDateStr = "NA";
        let isSignup = false;
        
        if (userdetails?.userData?.timeJoined) {
          const joinedTime = new Date(userdetails.userData.timeJoined);
          createdDateStr = joinedTime.toISOString();
          
          // If the account was created within the last 5 minutes, it's a fresh sign-up!
          if (Date.now() - joinedTime.getTime() < 5 * 60 * 1000) {
            isSignup = true;
          }
        }

        const marketingConsent = userdetails?.metadata?.receiveMarketingAndPromitionalEmail || false;

        const eventPayload = {
          event: isSignup ? "sign_up" : "login",
          method: loginMethod,
          uuid: userUuid,
          account_created_date: createdDateStr,
          marketing_and_promotion_consent: marketingConsent
        };
        if (loginSource) {
          eventPayload.source = loginSource;
        }
        
        console.log(`DataLayer ${isSignup ? "Sign Up" : "Login"} Event:`, eventPayload);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(eventPayload);

        sessionStorage.removeItem("sso_login_initiated_source");
      }
    }
  }, [isLoggedIn, userdetails]);

  if (!isLoggedIn || !aKey) {
    return null;
  }

  const email = userdetails?.userData?.emails?.[0] || userdetails?.email;
  const phone = userdetails?.userData?.phoneNumbers?.[0] || userdetails?.phone;

  return (
    <AccessType
      accessTypeKey={aKey}
      accessTypeBkIntegrationId={ck.accesstype_subauth || ""}
      enableAccesstype={true}
      email={email}
      phone={phone}
    >
      {accessTypeProps => <SyncerInner {...accessTypeProps} />}
    </AccessType>
  );
};
